import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { api } from '../api/client';
import { useReconnectingWebSocket } from '../features/chat/realtime';
import { useAuth } from './auth';

interface NotificationEvent {
  event: 'notification.created' | 'notification.counts.updated';
  unread_messages_count?: number;
  unread_notifications_count?: number;
}

interface ChatStatusContextValue {
  eventVersion: number;
  refresh: () => Promise<void>;
  unreadMessages: number;
  unreadNotifications: number;
}

const ChatStatusContext = createContext<ChatStatusContextValue | null>(null);

export function ChatStatusProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [eventVersion, setEventVersion] = useState(0);

  const refresh = useCallback(async () => {
    if (auth.status !== 'authenticated') {
      setUnreadMessages(0);
      setUnreadNotifications(0);
      return;
    }
    const [messageCount, notificationCount] = await Promise.all([
      api<{ total_unread: number }>('/api/chats/unread-count/'),
      api<{ total_unread: number }>('/api/notifications/unread-count/'),
    ]);
    setUnreadMessages(messageCount.total_unread);
    setUnreadNotifications(notificationCount.total_unread);
  }, [auth.status, auth.user?.id]);

  useEffect(() => {
    void refresh().catch(() => {});
  }, [refresh]);

  useReconnectingWebSocket<NotificationEvent>(
    auth.status === 'authenticated' ? '/ws/notifications/' : null,
    (event) => {
      if (typeof event.unread_messages_count === 'number') setUnreadMessages(event.unread_messages_count);
      if (typeof event.unread_notifications_count === 'number') {
        setUnreadNotifications(event.unread_notifications_count);
      } else if (event.event === 'notification.created') {
        setUnreadNotifications((count) => count + 1);
      }
      setEventVersion((version) => version + 1);
    },
    () => { void refresh().catch(() => {}); },
  );

  const value = useMemo(
    () => ({ eventVersion, refresh, unreadMessages, unreadNotifications }),
    [eventVersion, refresh, unreadMessages, unreadNotifications],
  );
  return <ChatStatusContext.Provider value={value}>{children}</ChatStatusContext.Provider>;
}

export function useChatStatus() {
  const value = useContext(ChatStatusContext);
  if (!value) throw new Error('useChatStatus must be used inside ChatStatusProvider');
  return value;
}
